import React, { useState, useRef, useCallback } from "react";
import "./App.css";
import UploadScreen from "./components/UploadScreen";
import VideoPlayer from "./components/VideoPlayer";
import HighlightEditor from "./components/HighlightEditor";
import FileSplitter from "./components/FileSplitter";
import ProgressIndicator from "./components/ProgressIndicator";
import MusicSelection from "./components/MusicSelection";
import QualityModal from "./components/QualityModal";
import Logo from "./components/Logo";
import useHighlights from "./hooks/useHighlights";
import useTextOverlays from "./hooks/useTextOverlays";
import useObjectUrl from "./hooks/useObjectUrl";
import { generateHighlightVideo, downloadBlob } from "./services/ffmpeg";

const App = () => {
  const [videoFile, setVideoFile] = useState(null);
  const [highlightBlob, setHighlightBlob] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState("");
  const [showQualityModal, setShowQualityModal] = useState(false);
  const [progress, setProgress] = useState(0);
  const [processingStep, setProcessingStep] = useState("");
  const [appMode, setAppMode] = useState("highlighter");
  const [showMusicModal, setShowMusicModal] = useState(false);
  const [selectedMusic, setSelectedMusic] = useState(null);
  const videoRef = useRef(null);

  // Object URLs are created and revoked alongside the blobs they represent.
  const videoUrl = useObjectUrl(videoFile);
  const highlightVideoUrl = useObjectUrl(highlightBlob);

  const {
    highlights,
    selectedHighlight,
    setSelectedHighlight,
    activeHighlight,
    handleAddHighlight,
    handleSelectHighlight,
    handleUpdateHighlight,
    handleDeleteHighlight,
    handleHighlightClick,
    handleOutputHighlightClick,
    handleMouseDown,
    handleDuplicateHighlight,
    resetHighlights,
  } = useHighlights(videoRef);

  const clearHighlightSelection = useCallback(() => setSelectedHighlight(null), [setSelectedHighlight]);

  const {
    textOverlays,
    selectedTextOverlay,
    handleAddTextOverlay,
    handleSelectTextOverlay,
    handleUpdateTextOverlay,
    handleDeleteTextOverlay,
    resetTextOverlays,
  } = useTextOverlays(videoRef, clearHighlightSelection);

  const handleMusicSelection = useCallback((musicConfig) => {
    setSelectedMusic(musicConfig);
    setShowMusicModal(false);
    setShowQualityModal(true);
  }, []);

  const handleSkipMusic = useCallback(() => {
    setSelectedMusic(null);
    setShowMusicModal(false);
    setShowQualityModal(true);
  }, []);

  // Files of any size are processed directly: FFmpeg mounts the File and
  // reads from it, so the source is never copied into memory.
  const handleVideoUpload = useCallback((file) => {
    setVideoFile(file);
  }, []);

  const handleOpenSplitter = useCallback(() => {
    setAppMode("splitter");
  }, []);

  const handleGenerateVideo = useCallback(async (preset = "fast") => {
    if (!highlights.length || !videoFile) return;

    setIsGenerating(true);
    setGenerationError("");
    setShowQualityModal(false);
    setHighlightBlob(null);
    setProgress(0);

    try {
      const { blob, filename } = await generateHighlightVideo({
        highlights,
        videoFile,
        selectedMusic,
        preset,
        onProgress: setProgress,
        onStep: setProcessingStep,
      });
      setHighlightBlob(blob);
      downloadBlob(blob, filename);
    } catch (error) {
      console.error("Error generating video:", error);
      setGenerationError(error.message);
    } finally {
      setIsGenerating(false);
      setSelectedMusic(null);
    }
  }, [highlights, videoFile, selectedMusic]);

  const handleGenerateClick = useCallback(() => {
    setShowMusicModal(true);
  }, []);

  const handleBackToHighlighter = useCallback(() => {
    setAppMode("highlighter");
    setVideoFile(null);
    setHighlightBlob(null);
    setGenerationError("");
    resetHighlights();
    resetTextOverlays();
  }, [resetHighlights, resetTextOverlays]);

  const handleQualityCancel = useCallback(() => {
    setShowQualityModal(false);
  }, []);

  const renderContent = () => {
    if (appMode === "splitter") {
      return <FileSplitter onBackToMain={handleBackToHighlighter} />;
    }

    if (videoFile && videoUrl) {
      return (
        <div className="video-editor">
          <Logo />

          <VideoPlayer
            videoFile={videoUrl}
            videoRef={videoRef}
            highlights={highlights}
            textOverlays={textOverlays}
            onDeleteHighlight={handleDeleteHighlight}
            onSelectHighlight={handleSelectHighlight}
            onUpdateHighlight={handleUpdateHighlight}
            onDeleteTextOverlay={handleDeleteTextOverlay}
            onSelectTextOverlay={handleSelectTextOverlay}
            onUpdateTextOverlay={handleUpdateTextOverlay}
            selectedHighlight={selectedHighlight}
            selectedTextOverlay={selectedTextOverlay}
            activeHighlight={activeHighlight}
            onHighlightClick={handleHighlightClick}
            onOutputHighlightClick={handleOutputHighlightClick}
            onMouseDown={handleMouseDown}
            onAddHighlight={handleAddHighlight}
            onAddTextOverlay={handleAddTextOverlay}
          />

          {selectedHighlight && (
            <HighlightEditor
              highlight={selectedHighlight}
              onUpdate={handleUpdateHighlight}
              onDelete={handleDeleteHighlight}
              onDuplicate={handleDuplicateHighlight}
              onGenerateHighlight={handleGenerateClick}
            />
          )}

          {isGenerating && (
            <ProgressIndicator progress={progress} isGenerating={isGenerating} currentStep={processingStep} />
          )}

          {generationError && (
            <div className="error-message generation-error">Error generating video: {generationError}</div>
          )}

          {highlightVideoUrl && (
            <div className="highlight-player">
              <h2>Highlights</h2>
              <video src={highlightVideoUrl} controls className="video" />
            </div>
          )}
        </div>
      );
    }

    return <UploadScreen onUpload={handleVideoUpload} onOpenSplitter={handleOpenSplitter} />;
  };

  return (
    <div className="App">
      <QualityModal isVisible={showQualityModal} onSelect={handleGenerateVideo} onCancel={handleQualityCancel} />

      {showMusicModal && (
        <MusicSelection
          isVisible={showMusicModal}
          onClose={() => setShowMusicModal(false)}
          onAddMusic={handleMusicSelection}
          onSkipMusic={handleSkipMusic}
          estimatedDuration={highlights.reduce((total, h) => total + h.duration * (h.slowMotion ? 2 : 1), 0)}
        />
      )}

      {renderContent()}
    </div>
  );
};

export default App;
